import { Modal } from '@stormwater-software/lib-ui';
import React from 'react';
import ReactDOM from 'react-dom';
import userApi from '../../../utils/api/user';
import {
  Deactivation,
  forgetSavedSession,
  wipeDeactivatedUser,
} from '../../../utils/offlineSession';

type SessionEndReason = 'deactivated' | 'expired';

const DIALOGS: Record<SessionEndReason, { title: string; message: string }> = {
  deactivated: {
    title: 'Account deactivated',
    message:
      'Your account has been deactivated. The projects downloaded to this device, and any work not yet uploaded, have been removed. Contact your administrator if you think this is a mistake.',
  },
  expired: {
    title: 'Session ended',
    message:
      'Your session has ended. Sign in again to continue. Your offline work is still saved on this device and will upload after you sign in.',
  },
};

/**
 * Shown in its own root, outside the app tree: at start-up the app has not
 * mounted yet, and a redirect straight after a toast would swallow it.
 * Resolves when the user presses OK; it cannot be dismissed any other way.
 */
const showSessionEndedDialog = (reason: SessionEndReason) =>
  new Promise<void>((resolve) => {
    const host = document.createElement('div');
    const { title, message } = DIALOGS[reason];

    const close = () => {
      ReactDOM.unmountComponentAtNode(host);
      host.remove();
      resolve();
    };

    document.body.appendChild(host);
    ReactDOM.render(
      <Modal
        show
        isSmall
        title={title}
        disableClose
        cancelBtn={false}
        submitBtnText='OK'
        dismissOnBgClick={false}
        dismissOnEsc={false}
        handleSubmit={close}
        handleClose={() => {}}
      >
        <p>{message}</p>
      </Modal>,
      host,
    );
  });

/**
 * SW2-2474 — the server says the session is over: tell the user why, then log
 * out and go to the login page.
 *
 * The device is cleaned up before the dialog, so closing the app on it changes
 * nothing: a deactivated user's downloads are already gone (or, if the delete
 * failed, are deleted at the next start), and an ended session can no longer be
 * restored offline. Otherwise the work is kept. A deactivation of someone other
 * than this device's user is an ordinary end of session here.
 */
export async function endSession(deactivation: Deactivation | null) {
  const deactivated = Boolean(deactivation) && (await wipeDeactivatedUser(deactivation!));

  if (!deactivated) forgetSavedSession();

  await showSessionEndedDialog(deactivated ? 'deactivated' : 'expired');

  userApi.logout();
  window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
}
